'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  type BaseArtifactDto,
  BrowserRecordingArtifactMetadataSchema,
  BrowserScreenshotArtifactMetadataSchema,
} from '@terra/shared-contracts-artifact';

const ARTIFACT_FILENAME_REGEX =
  /^(?:.*\/)?([0-9a-f-]{36})_(.+)_(action|result)\.txt$/;

function stripToolCallPrefix(toolCallId: string): string {
  const firstUnderscoreIndex = toolCallId.indexOf('_');

  return firstUnderscoreIndex === -1
    ? toolCallId
    : toolCallId.slice(firstUnderscoreIndex + 1);
}

function extractRuntimeIdFromFileName(
  fileName: string | null,
): string | undefined {
  if (!fileName) return undefined;
  const match = fileName.match(ARTIFACT_FILENAME_REGEX);

  return match?.[1];
}

function indexByToolCallId({
  artifacts,
  matches,
}: {
  artifacts: BaseArtifactDto[];
  matches: (artifact: BaseArtifactDto) => boolean;
}): Map<string, BaseArtifactDto> {
  return new Map(
    artifacts.flatMap((artifact) =>
      artifact.toolCallId && matches(artifact)
        ? [
            [artifact.toolCallId, artifact] as const,
            [stripToolCallPrefix(artifact.toolCallId), artifact] as const,
          ]
        : [],
    ),
  );
}

type ArtifactsContextType = {
  getArtifactById: (id: string) => BaseArtifactDto | undefined;
  getArtifactByToolCallId: (toolCallId: string) => BaseArtifactDto | undefined;
  getRecordingByToolCallId: (toolCallId: string) => BaseArtifactDto | undefined;
  getScreenshotByToolCallId: (
    toolCallId: string,
  ) => BaseArtifactDto | undefined;
  getArtifactByRuntimeId: (runtimeId: string) => BaseArtifactDto | undefined;
  isEvidence: (artifact: BaseArtifactDto | undefined) => boolean;
};

const ArtifactsContext = createContext<ArtifactsContextType | undefined>(
  undefined,
);

type ArtifactsProviderProps = {
  children: ReactNode;
  artifacts?: BaseArtifactDto[];
  evidenceArtifactIds?: Set<string>;
};

export function ArtifactsProvider({
  children,
  artifacts = [],
  evidenceArtifactIds,
}: ArtifactsProviderProps) {
  const artifactsById = useMemo(() => {
    const map = new Map<string, BaseArtifactDto>();

    for (const artifact of artifacts) {
      map.set(artifact.id, artifact);
    }

    return map;
  }, [artifacts]);

  const artifactsByToolCallId = useMemo(() => {
    const map = new Map<string, BaseArtifactDto>();

    for (const artifact of artifacts) {
      if (artifact.toolCallId) {
        map.set(artifact.toolCallId, artifact);
      }
    }

    return map;
  }, [artifacts]);

  const recordingsByToolCallId = useMemo(
    () =>
      indexByToolCallId({
        artifacts,
        matches: (artifact) =>
          BrowserRecordingArtifactMetadataSchema.safeParse(artifact.metadata)
            .success,
      }),
    [artifacts],
  );

  const screenshotsByToolCallId = useMemo(
    () =>
      indexByToolCallId({
        artifacts,
        matches: (artifact) =>
          BrowserScreenshotArtifactMetadataSchema.safeParse(artifact.metadata)
            .success,
      }),
    [artifacts],
  );

  const artifactsByRuntimeId = useMemo(() => {
    const map = new Map<string, BaseArtifactDto>();

    for (const artifact of artifacts) {
      const id =
        artifact.runtimeId ??
        extractRuntimeIdFromFileName(artifact.actionFileName);

      if (id) {
        map.set(id, artifact);
      }
    }

    return map;
  }, [artifacts]);

  const getArtifactById = useMemo(
    () => (id: string) => artifactsById.get(id),
    [artifactsById],
  );

  const getArtifactByToolCallId = useMemo(
    () => (toolCallId: string) => artifactsByToolCallId.get(toolCallId),
    [artifactsByToolCallId],
  );

  const getRecordingByToolCallId = useMemo(
    () => (toolCallId: string) => recordingsByToolCallId.get(toolCallId),
    [recordingsByToolCallId],
  );

  const getScreenshotByToolCallId = useMemo(
    () => (toolCallId: string) => screenshotsByToolCallId.get(toolCallId),
    [screenshotsByToolCallId],
  );

  const getArtifactByRuntimeId = useMemo(
    () => (runtimeId: string) => artifactsByRuntimeId.get(runtimeId),
    [artifactsByRuntimeId],
  );

  const isEvidence = useMemo(
    () => (artifact: BaseArtifactDto | undefined) => {
      if (!artifact || !evidenceArtifactIds) {
        return false;
      }

      return (
        evidenceArtifactIds.has(artifact.id) ||
        (artifact.runtimeId
          ? evidenceArtifactIds.has(artifact.runtimeId)
          : false)
      );
    },
    [evidenceArtifactIds],
  );

  const contextValue: ArtifactsContextType = useMemo(
    () => ({
      getArtifactById,
      getArtifactByToolCallId,
      getRecordingByToolCallId,
      getScreenshotByToolCallId,
      getArtifactByRuntimeId,
      isEvidence,
    }),
    [
      getArtifactById,
      getArtifactByToolCallId,
      getRecordingByToolCallId,
      getScreenshotByToolCallId,
      getArtifactByRuntimeId,
      isEvidence,
    ],
  );

  return (
    <ArtifactsContext.Provider value={contextValue}>
      {children}
    </ArtifactsContext.Provider>
  );
}

export function useArtifacts() {
  const context = useContext(ArtifactsContext);

  if (context === undefined) {
    throw new Error('useArtifacts must be used within an ArtifactsProvider');
  }

  return context;
}

export function useArtifactsOptional() {
  return useContext(ArtifactsContext);
}
